import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, ShieldCheck, ShieldSlash, Warning, ArrowClockwise, Gear,
  MagnifyingGlass, ArrowsClockwise,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const ROLE_LABELS = {
  owner: 'Owner', admin: 'Admin', assistant: 'Assistant', accountant: 'CA / Accountant',
  staff: 'Staff', sub_staff: 'Sub-Staff',
  distributor: 'Distributor', sales_person: 'Sales Person',
  gift_card_vendor: 'Gift Card Vendor', card_vendor: 'Card Vendor',
  recharge_vendor: 'Recharge Vendor', electricity_vendor: 'Electricity Vendor',
};

const STATUS_STYLES = {
  enabled: { icon: ShieldCheck, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  pending: { icon: Warning, cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  disabled: { icon: ShieldSlash, cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

function fmtDT(s) {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
}

export default function TwoFactorAdminPage() {
  const [users, setUsers] = useState([]);
  const [policy, setPolicy] = useState({ required_roles: [], force_enrollment_enabled: false, grace_period_days: 14 });
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [resettingId, setResettingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listR, polR] = await Promise.all([
        api.get('/auth/2fa/admin/users'),
        api.get('/auth/2fa/policy'),
      ]);
      setUsers(listR.data?.users || []);
      setPolicy(polR.data?.policy || {});
      setAllRoles(polR.data?.all_roles || []);
      setAccessDenied(false);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error(err?.response?.data?.detail || 'Load failed');
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const r = await api.put('/auth/2fa/policy', {
        required_roles: policy.required_roles,
        force_enrollment_enabled: policy.force_enrollment_enabled,
        grace_period_days: policy.grace_period_days,
      });
      setPolicy(r.data.policy);
      toast.success('Policy saved. Affected users will be soft-blocked on next login.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSavingPolicy(false); }
  };

  const toggleRole = (r) => {
    if (r === 'owner') return;
    setPolicy((p) => {
      const has = (p.required_roles || []).includes(r);
      return {
        ...p,
        required_roles: has ? p.required_roles.filter((x) => x !== r) : [...(p.required_roles || []), r],
      };
    });
  };

  const resetUser = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.name} (${u.email})? Wo agle login pe dobara enroll karega.`)) return;
    setResettingId(u.id);
    try {
      await api.post(`/auth/2fa/admin-reset/${u.id}`);
      toast.success(`${u.name} ka 2FA reset ho gaya`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally { setResettingId(null); }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [users, roleFilter, statusFilter, search]);

  const counts = useMemo(() => {
    const c = { total: users.length, enabled: 0, pending: 0, disabled: 0 };
    users.forEach((u) => { c[u.status] = (c[u.status] || 0) + 1; });
    return c;
  }, [users]);

  return (
    <div className="space-y-5" data-testid="twofa-admin-page">
      {accessDenied && (
        <div className="max-w-lg mx-auto mt-8 p-6 border-2 border-rose-300 bg-rose-50 rounded-xl text-center" data-testid="twofa-admin-403">
          <ShieldSlash size={40} weight="fill" className="mx-auto text-rose-600 mb-2" />
          <div className="font-bold text-rose-900 text-lg">Access Denied</div>
          <p className="text-sm text-rose-800 mt-1">Ye page sirf Owner / Admin ke liye hai. Aapke role ke paas access nahi hai.</p>
          <Link to="/"><Button className="mt-3 bg-rose-600 hover:bg-rose-700">Home pe waapas jao</Button></Link>
        </div>
      )}
      {!accessDenied && (<>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield size={24} weight="duotone" className="text-violet-700" />
            2FA Admin Panel
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Har user ka 2FA status dekho, reset karo, aur role-level enforcement policy control karo.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/security/2fa">
            <Button variant="outline" size="sm"><Shield size={14} className="mr-1" /> My 2FA</Button>
          </Link>
          <Button variant="outline" size="sm" onClick={load} data-testid="admin-2fa-refresh"><ArrowClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard color="gray" icon={Shield} label="Total Users" value={counts.total} />
        <SummaryCard color="emerald" icon={ShieldCheck} label="Enabled" value={counts.enabled} />
        <SummaryCard color="amber" icon={Warning} label="Pending" value={counts.pending} />
        <SummaryCard color="rose" icon={ShieldSlash} label="Disabled" value={counts.disabled} />
      </div>

      {/* Policy Card */}
      <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Gear size={18} className="text-indigo-600" />
            <h2 className="font-bold text-gray-900">Role-based Enforcement Policy</h2>
          </div>
          <div className="text-[10px] text-gray-500">
            {policy.updated_by ? `Last updated by ${policy.updated_by} · ${fmtDT(policy.updated_at)}` : 'Never updated'}
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm bg-indigo-50 border border-indigo-200 rounded p-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!policy.force_enrollment_enabled}
            onChange={(e) => setPolicy({ ...policy, force_enrollment_enabled: e.target.checked })}
            data-testid="policy-force-toggle"
            className="mt-1"
          />
          <div>
            <div className="font-semibold text-indigo-900">Force 2FA enrollment for selected roles</div>
            <div className="text-[11px] text-indigo-800 mt-0.5">
              Jab ON hoga, selected roles ke users bina 2FA enable kiye main app use nahi kar payenge — login ke baad seedhe <code className="bg-white px-1 rounded">/security/2fa</code> pe redirect ho jayenge.
            </div>
          </div>
        </label>

        <div>
          <div className="text-xs font-semibold text-gray-600 uppercase mb-2">Required Roles</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {allRoles.map((r) => {
              const checked = (policy.required_roles || []).includes(r);
              const isOwner = r === 'owner';
              return (
                <label
                  key={r}
                  className={`flex items-center gap-2 p-2 border rounded text-xs cursor-pointer ${
                    checked ? 'bg-violet-50 border-violet-300' : 'bg-white border-gray-200'
                  } ${isOwner ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isOwner}
                    onChange={() => toggleRole(r)}
                    data-testid={`policy-role-${r}`}
                  />
                  <span className="font-medium">{ROLE_LABELS[r] || r}</span>
                  {isOwner && <span className="text-[9px] text-gray-500">(always)</span>}
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase block mb-1">Grace Period (days)</label>
            <Input
              type="number"
              min={0}
              max={60}
              value={policy.grace_period_days ?? 14}
              onChange={(e) => setPolicy({ ...policy, grace_period_days: Number(e.target.value) || 0 })}
              className="w-32 h-9"
              data-testid="policy-grace-days"
            />
          </div>
          <div className="flex-1" />
          <Button
            onClick={savePolicy}
            disabled={savingPolicy}
            className="bg-violet-600 hover:bg-violet-700"
            data-testid="policy-save"
          >
            {savingPolicy ? 'Saving…' : 'Save Policy'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="pl-8 h-9"
            data-testid="admin-2fa-search"
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-9 px-3 border rounded text-sm bg-white" data-testid="admin-2fa-role-filter">
          <option value="all">All Roles</option>
          {allRoles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 border rounded text-sm bg-white" data-testid="admin-2fa-status-filter">
          <option value="all">All Status</option>
          <option value="enabled">Enabled</option>
          <option value="pending">Pending</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Users table */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">Koi user match nahi hua</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">2FA Status</th>
                  <th className="px-3 py-2 text-left">Enabled On</th>
                  <th className="px-3 py-2 text-left">Reset Info</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const S = STATUS_STYLES[u.status] || STATUS_STYLES.disabled;
                  const Icon = S.icon;
                  return (
                    <tr key={u.id} className="border-t hover:bg-gray-50" data-testid={`admin-2fa-row-${u.id}`}>
                      <td className="px-3 py-2 font-semibold text-gray-900">{u.name}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{u.email}</td>
                      <td className="px-3 py-2">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded border ${S.cls}`}>
                          <Icon size={11} weight="fill" /> {u.status.toUpperCase()}
                        </span>
                        {u.role_required && !u.two_factor_enabled && (
                          <div className="text-[9px] text-amber-700 mt-0.5">Role requires enrollment</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[11px] text-gray-500">{fmtDT(u.enabled_at)}</td>
                      <td className="px-3 py-2 text-[10px] text-gray-500">
                        {u.reset_at ? <>By {u.reset_by || '—'}<br />{fmtDT(u.reset_at)}</> : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {u.two_factor_enabled ? (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resettingId === u.id}
                            onClick={() => resetUser(u)}
                            className="text-rose-600 border-rose-300 hover:bg-rose-50"
                            data-testid={`admin-2fa-reset-${u.id}`}
                          >
                            <ArrowsClockwise size={12} className="mr-1" />
                            {resettingId === u.id ? 'Resetting…' : 'Reset 2FA'}
                          </Button>
                        ) : (
                          <span className="text-[10px] text-gray-400">Not enabled</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>)}
    </div>
  );
}

function SummaryCard({ color, icon: Icon, label, value }) {
  const map = {
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  };
  return (
    <div className={`border rounded-lg p-3 ${map[color] || map.gray}`}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="duotone" />
        <span className="text-[10px] uppercase font-bold opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold mt-1">{value ?? 0}</div>
    </div>
  );
}
